import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import SubmitHero from '../components/submit/SubmitHero';
import SubmissionForm from '../components/submit/SubmissionForm';
import { createRecipe, updateRecipe } from '../services/recipeService';
import { useLanguage } from '../i18n/LanguageContext';
import './SubmitPage.css';

const SubmitPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useOutletContext() ?? {};
  const { lang, t } = useLanguage();
  const s = t.submit;
  const explorerPath = lang === 'he' ? '/heb/explorer' : '/explorer';
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editingRecipe = location.state?.recipe ?? null;
  const editingRecipeId = editingRecipe?._id || editingRecipe?.id;
  const isEditMode = Boolean(location.state?.mode === 'edit' && editingRecipeId);

  const heroContent = useMemo(
    () =>
      isEditMode
        ? { title: s.editHeroTitle, description: s.editHeroDescription }
        : { title: s.heroTitle, description: s.heroDescription },
    [isEditMode, s]
  );

  const submitHeroContent = heroContent;

  const handleRecipeSubmit = async ({ payload, imageFile }) => {
    setIsSubmitting(true);
    setSubmissionStatus(null);

    try {
      const successMessage = isEditMode ? s.successEdit : s.successCreate;

      if (isEditMode && editingRecipeId) {
        const updatedRecipe = await updateRecipe(editingRecipeId, payload, imageFile);
        setSubmissionStatus({ type: 'success', message: successMessage });
        const savedId = updatedRecipe?._id || updatedRecipe?.id || editingRecipeId;
        const detailPath = lang === 'he' ? `/heb/recipes/${savedId}` : `/recipes/${savedId}`;
        navigate(detailPath, { replace: true, state: { recipe: updatedRecipe } });
      } else {
        await createRecipe(payload, imageFile);
        setSubmissionStatus({ type: 'success', message: successMessage });
        navigate(explorerPath, { replace: true });
      }
    } catch (err) {
      const message = err?.message ?? (isEditMode ? s.errorEdit : s.errorCreate);
      setSubmissionStatus({ type: 'error', message });
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="submit-page">
      <main className="submit-main">
        <SubmitHero {...submitHeroContent} />
        <section className="contribution-card">
          {isEditMode ? (
            <div className="contribution-card-head">
              <span>{s.cardSpanEdit}</span>
              <h2>{s.cardHeadEdit}</h2>
            </div>
          ) : null}
          {submissionStatus ? (
            <div
              className={`submission-alert ${submissionStatus.type}`}
              role="status"
              aria-live="polite"
            >
              {submissionStatus.message}
            </div>
          ) : null}
          <SubmissionForm
            onSubmit={handleRecipeSubmit}
            onCancel={() => navigate(explorerPath)}
            isSubmitting={isSubmitting}
            initialRecipe={editingRecipe}
            isEditMode={isEditMode}
            submitLabel={isEditMode ? s.submitButtonEdit : s.submitButtonCreate}
            currentUser={currentUser}
          />
        </section>
      </main>
      <PageFooter />
    </div>
  );
};

export default SubmitPage;
